import { ReactNode, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { AccessRequestClientModel, RequestAgainClientModel, useRequestAgainAccessRequest } from '@apono/client-app-sdk';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import routes from '@routes';
import { Button, Stack } from '@mui/material';
import { AnalyticsEvents } from '@common/utils/analytics/events';
import { CustomField } from '@common/organisms/RequestCustomFields';
import { RequestFields } from '@common/organisms/RequestFields';
import { FormState, initialFormState } from '@MyRequests/helpers';
import AppModal from '@common/components/AppModal';
import RequestErrorBanner from '@NewRequest/components/RequestErrorBanner';

export default function RequestAgain({
  request,
  buttonText,
}: {
  request: AccessRequestClientModel;
  buttonText?: string;
}) {
  const [opened, setOpened] = useState(false);
  const { requestAgainAccessRequest, isLoading, isSuccess, isError, error } = useRequestAgainAccessRequest(request.id);

  const navigate = useNavigate();
  useEffect(() => {
    if (isSuccess) {
      toast.success(`Request ${request.id} requested again`);
      navigate(routes.OpenRequests.path);
    }
  }, [isSuccess, navigate, request.id]);

  return (
    <>
      <LoadingButton
        data-trigger={AnalyticsEvents.RequestAgainClicked}
        data-props={JSON.stringify({ requestId: request.id })}
        variant="outlined"
        size="small"
        onClick={() => setOpened(true)}
        loading={isLoading}
      >
        {buttonText || 'Request again'}
      </LoadingButton>
      {opened && (
        <RequestAgainConfirmModal
          onClose={() => setOpened(false)}
          onConfirm={(state: RequestAgainClientModel) => requestAgainAccessRequest(state)}
          errorBanner={isError && <RequestErrorBanner error={error} />}
        />
      )}
    </>
  );
}

function RequestAgainConfirmModal({
  onClose,
  onConfirm,
  errorBanner,
}: {
  onClose: () => void;
  onConfirm: (state: RequestAgainClientModel) => void;
  errorBanner?: ReactNode;
}) {
  const [formState, setFormState] = useState<FormState>(initialFormState);

  const [isDryRunErrors, setIsDryRunErrors] = useState(false);

  function updateFormState<T extends keyof FormState>(field: T, value: FormState[T] | CustomField): void {
    setFormState((prevState) => {
      return { ...prevState, [field]: value };
    });
  }

  const handleConfirm = () => onConfirm(formState);

  return (
    <AppModal open onClose={onClose} title={'You will resubmit the same access request for approval.'}>
      <Stack direction="column" justifyContent={'space-between'} spacing={2}>
        {errorBanner}
        <RequestFields
          formState={formState}
          updateFormState={updateFormState}
          withDuration={false}
          onDryRunErrorsChange={(isDryRunErrors) => setIsDryRunErrors(isDryRunErrors)}
        />
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button variant="outlined" color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleConfirm} disabled={isDryRunErrors} type="submit">
            Submit
          </Button>
        </Stack>
      </Stack>
    </AppModal>
  );
}
